import { BaseShader } from "webgl-framework";
import { ShaderInputs } from "./ShaderInputs";
import { engine } from "../KotlinLib";

/**
 * Test shader: simple Lambertian directional lighting of a white material.
 *
 * The light is straight above the scene: it travels in (0, 0, -1) in renderer
 * space (Z-up), so the vector towards it is (0, 0, 1) and the Lambert term is
 * max(dot(N, +Z), 0) — surfaces facing up are white, surfaces facing sideways
 * or down are black. No texture and no ambient term.
 *
 * Normals are transformed to world space by the model matrix (the merged group
 * meshes already store world-space normals and are drawn with an identity
 * transform). Unlike the model normals test shader, this one has to apply that
 * transform, otherwise instances rotated around X/Y would be lit wrongly.
 *
 * Uniforms: [view_proj_matrix, model_matrix].
 */
export class TestLambertShader extends BaseShader implements ShaderInputs {
    view_proj_matrix: WebGLUniformLocation | undefined;
    model_matrix: WebGLUniformLocation | undefined;
    rm_Vertex: number | undefined;
    rm_Normal: number | undefined;

    public attributes?: number[];
    public typedAttributes?: { [key: number]: number };
    public uniforms?: WebGLUniformLocation[];

    /** @inheritdoc */
    fillCode() {
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform mat4 model_matrix;
            attribute vec4 rm_Vertex;
            attribute vec3 rm_Normal;

            varying float vLambertian;

            const vec3 LIGHT_DIRECTION = vec3(0.0, 0.0, 1.0);
            const float halfLambertExponent = 0.85;

            void main() {
              gl_Position = view_proj_matrix * rm_Vertex;
              vec3 worldNormal = normalize((model_matrix * vec4(rm_Normal, 0.0)).xyz);
              float nDotL = max(dot(worldNormal, LIGHT_DIRECTION), 0.0);

              vLambertian = nDotL;

              float halfLambert = pow(nDotL * 0.5 + 0.5, halfLambertExponent);
              vLambertian = halfLambert;
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying float vLambertian;

            void main() {
              gl_FragColor = vec4(vec3(vLambertian), 1.0);
            }`;
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        this.rm_Vertex = this.getAttrib("rm_Vertex");
        this.rm_Normal = this.getAttrib("rm_Normal");

        this.view_proj_matrix = this.getUniform("view_proj_matrix");
        this.model_matrix = this.getUniform("model_matrix");

        this.attributes = [this.rm_Vertex, this.rm_Normal];
        this.typedAttributes = {
            [engine.AttributeType.VERTEX.ordinal]: this.rm_Vertex!,
            [engine.AttributeType.NORMAL.ordinal]: this.rm_Normal!
        };
        this.uniforms = [this.view_proj_matrix, this.model_matrix];
    }
}
